import React, { useState, useEffect, useMemo } from 'react';
import { useQuery } from '@apollo/client';
import {
  GET_ALL_STATES,
  GET_CITIES_BY_STATE,
  GET_ZIPCODES_BY_STATE_AND_CITY,
  GetAllStatesResponse,
  GetCitiesByStateResponse,
  GetZipcodesByStateAndCityResponse,
} from '../../../pickup/appointment/graphql/queries/getLocationData';
import InputFilterItem from '../../../delivery/components/InputFilterItem';
import ParcelFilterStyle from '../../../delivery/components/ParcelFilterStyle';
import { getSmartLabelClassName } from '../../../../utils/labelWidthUtils';
import { useTranslation } from 'react-i18next';

export interface FilterOptions {
  residentialAddress?: string;
  residentialName?: string;
  cityName?: string;
  stateAbbreviation?: string;
  zipcode?: string;
}

export const DEFAULT_FILTER_OPTIONS: FilterOptions = {
  residentialAddress: undefined,
  residentialName: undefined,
  cityName: undefined,
  stateAbbreviation: undefined,
  zipcode: undefined,
};

interface FilterAreaProps {
  filters: FilterOptions;
  setFilters: (filters: FilterOptions) => void;
}

const FilterArea: React.FC<FilterAreaProps> = ({ filters, setFilters }) => {
  const [cities, setCities] = useState<string[]>([]);
  const [zipcodes, setZipcodes] = useState<string[]>([]);
  const { t, i18n } = useTranslation();

  const allPlaceholder = useMemo(() => {
    const translated = t('components.input.all');
    return translated === 'components.input.all' ? (i18n.language === 'zh' ? '全部' : 'All') : translated;
  }, [t, i18n.language]);

  const { data: statesData } = useQuery<GetAllStatesResponse>(GET_ALL_STATES, {
    fetchPolicy: 'cache-and-network',
  });

  const { data: citiesData } = useQuery<GetCitiesByStateResponse>(GET_CITIES_BY_STATE, {
    variables: { state: filters.stateAbbreviation ?? '' },
    skip: !filters.stateAbbreviation,
    fetchPolicy: 'cache-and-network',
  });

  const { data: zipcodesData } = useQuery<GetZipcodesByStateAndCityResponse>(
    GET_ZIPCODES_BY_STATE_AND_CITY,
    {
      variables: {
        state: filters.stateAbbreviation ?? '',
        city: filters.cityName ?? '',
      },
      skip: !filters.stateAbbreviation || !filters.cityName,
      fetchPolicy: 'cache-and-network',
    }
  );

  useEffect(() => {
    if (citiesData?.getCitiesByState) {
      setCities(citiesData.getCitiesByState);
    } else {
      setCities([]);
    }
  }, [citiesData]);

  useEffect(() => {
    if (zipcodesData?.getZipcodesByStateAndCity) {
      setZipcodes(zipcodesData.getZipcodesByStateAndCity);
    } else {
      setZipcodes([]);
    }
  }, [zipcodesData]);

  const stateOptions = useMemo(() => {
    return [
      { value: '', label: allPlaceholder },
      ...(statesData?.getAllStates?.map((state) => ({ label: state, value: state })) ?? []),
    ];
  }, [statesData, allPlaceholder]);

  const cityOptions = useMemo(() => {
    return [
      { value: '', label: allPlaceholder },
      ...cities.map((city) => ({ label: city, value: city })),
    ];
  }, [cities, allPlaceholder]);

  const zipcodeOptions = useMemo(() => {
    return [
      { value: '', label: allPlaceholder },
      ...zipcodes.map((z) => ({ label: z, value: z })),
    ];
  }, [zipcodes, allPlaceholder]);

  const handleFilterChange = (name: keyof FilterOptions, value: string | undefined) => {
    const processed = (typeof value === 'string' && value.trim() === '') ? undefined : value;
    setFilters({ ...filters, [name]: processed });
  };

  const columnLabels = [
    t('mapTool.addressResidentialArea.filters.residentialAddress'),
    t('mapTool.addressResidentialArea.filters.residentialName'),
    t('mapTool.addressResidentialArea.filters.state'),
    t('mapTool.addressResidentialArea.filters.city'),
    t('mapTool.addressResidentialArea.filters.zipcode'),
  ];
  const labelClassName = getSmartLabelClassName(columnLabels);

  return (
    <div className="mb-2 pl-0 py-2">
      <div className="flex-1">
        <div className="grid grid-cols-3 gap-x-10 gap-y-4 items-start">
          <div className="min-w-0">
            <InputFilterItem
              label={t('mapTool.addressResidentialArea.filters.residentialAddress')}
              value={filters.residentialAddress ?? ''}
              onChange={(e) => handleFilterChange('residentialAddress', e.target.value)}
              spanClassName={labelClassName}
              placeholder={t('mapTool.addressResidentialArea.placeholders.residentialAddressFuzzy') as string}
            />
          </div>
          <div className="min-w-0">
            <InputFilterItem
              label={t('mapTool.addressResidentialArea.filters.residentialName')}
              value={filters.residentialName ?? ''}
              onChange={(e) => handleFilterChange('residentialName', e.target.value)}
              spanClassName={labelClassName}
              placeholder={t('mapTool.addressResidentialArea.placeholders.residentialNameFuzzy') as string}
            />
          </div>
          <div className="min-w-0">

              <InputFilterItem
                  label={t('mapTool.addressResidentialArea.filters.state')}
                  value={filters.stateAbbreviation ?? ''}
                  onChange={(e) => handleFilterChange('stateAbbreviation', e.target.value)}
                  spanClassName={labelClassName}
              />

          </div>
          <div className="min-w-0">


              <InputFilterItem
                  label={t('mapTool.addressResidentialArea.filters.city')}
                  value={filters.cityName ?? ''}
                  onChange={(e) => handleFilterChange('cityName', e.target.value)}
                  spanClassName={labelClassName}
              />

          </div>
          <div className="min-w-0">

              <InputFilterItem
                  label={t('mapTool.addressResidentialArea.filters.zipcode')}
                  value={filters.zipcode ?? ''}
                  onChange={(e) => handleFilterChange('zipcode', e.target.value)}
                  spanClassName={labelClassName}
              />


          </div>
        </div>
      </div>
    </div>
  );
};

export default FilterArea;
